/**
 * Plate & Hairline — shared rules.
 *
 * See docs/design-system/plate-and-hairline/ and PRODUCT.md.
 */

import { ARCHIVO_PLATE_METRICS, type PlateFontMetrics } from "./plate-and-hairline-metrics";

export interface SplitTitle {
  /** The part that goes on the plate. */
  display: string;
  /** The parenthetical, demoted to a subhead. Empty when the title has none. */
  descriptor: string;
  /**
   * The literal rest of the (trimmed) title, exactly as it appeared —
   * separator whitespace and parentheses included. The invariant callers may
   * rely on, and the SEO surface depends on:
   *
   *   display + remainder === title.trim()
   *
   * for EVERY input. Renderers must emit this verbatim rather than
   * re-assembling ` (${descriptor})`, which mangled "Foo(bar)" (gained a
   * space), "Foo (bar) extra" (gained a stray ")") and "Foo (bar" (gained a
   * paren). Empty when the whole title is on the plate.
   */
  remainder: string;
}

/**
 * Split a sound title into its plate portion and its subhead.
 *
 * This is what makes the giant-type signature viable. Measured across the 1,400
 * active rows, full titles run to a median of 39 characters and a maximum of 80,
 * which no plate can carry. But 83% follow the shape "Display Name (descriptor)",
 * and the display portion alone has a median of 16 and a 95th percentile of 29.
 *
 * So the plate renders the display portion and the parenthetical becomes the
 * subhead. See §10.3 for the distribution and §21 for the rendered case.
 *
 * Splits at the FIRST "(" only. A title with several parentheticals keeps the
 * later ones in the descriptor rather than dropping them, because dropping
 * content silently is worse than a long subhead.
 *
 * Titles with no parenthetical (17% of the catalog) return the whole string as
 * display and an empty descriptor. Callers must handle an absent descriptor
 * rather than assuming one.
 *
 * Only the clean shape "Display (descriptor)" — where the title ENDS at the
 * closing paren — is split. Anything else (text after the last paren, an
 * unclosed paren) keeps the whole title on the plate, because demoting half a
 * parenthetical produces a subhead that reads as a typo. The split is exact
 * slices, never re-assembly: `display + remainder` reconstructs the trimmed
 * title byte for byte, which is what keeps the h1's textContent — an SEO
 * surface on ~30 route families — identical to the original title.
 */
export function splitTitle(title: string): SplitTitle {
  const raw = (title ?? "").trim();
  const open = raw.indexOf("(");

  const whole: SplitTitle = { display: raw, descriptor: "", remainder: "" };

  if (open === -1) {
    return whole;
  }

  // Not the clean "Display (descriptor)" shape: the parenthetical must close
  // the title. "Foo (bar) extra" and the unclosed "Foo (bar" both fall through
  // to the whole title on the plate rather than a mangled subhead.
  if (!raw.endsWith(")")) {
    return whole;
  }

  // Exact slices. `display` drops the separator whitespace (the plate should
  // not end in a space); `remainder` starts where `display` ends, so the
  // separator — whatever it was, including none at all in "Foo(bar)" — is
  // preserved verbatim rather than normalised to a single space.
  const display = raw.slice(0, open).trimEnd();

  // A title that opens with "(" would leave the plate empty. Keep the whole
  // string on the plate instead: an odd-looking plate beats a blank one.
  if (!display) {
    return whole;
  }

  const remainder = raw.slice(display.length);
  const descriptor = raw.slice(open + 1, raw.length - 1).trim();

  return { display, descriptor, remainder };
}

/**
 * Archivo's average advance width as a fraction of font size, at the plate's
 * tracking. §10.2 owns this constant; do not restate it elsewhere.
 *
 * Only the character-count model uses it now (a `solvePlate` call without
 * `text`). Given the string, the solver measures it against the font's own
 * advance table instead — see `plateLineWidth` — because an average is wrong
 * for every specific string, and being wrong by half a line is what made the
 * server ship a 31px fallback heading the client re-solved to a 100px plate.
 */
const COEF = 0.68;

/** The plate's tracking, in em. Mirrors `letterSpacing` in plate.tsx. */
export const PLATE_TRACKING = -0.05;

/**
 * Width of one line of plate type, in px, as the browser will lay it out.
 *
 * The plate is uppercase, weight 900, tracked −0.05em, and Archivo kerns; the
 * measured table carries all four. Chromium adds the tracking after EVERY
 * glyph, the last one included, and the self-check in
 * scripts/qa/plate-font-metrics.mjs confirms this arithmetic reproduces the
 * rendered width to 0.01px at 100px for every heading it samples. A glyph the
 * table does not carry costs the A–Z average, which is the old model's
 * behaviour confined to that one glyph.
 */
export function plateLineWidth(
  text: string,
  size: number,
  metrics: PlateFontMetrics = ARCHIVO_PLATE_METRICS,
): number {
  let units = 0;
  let prev: string | null = null;
  for (const ch of text.toUpperCase()) {
    units += metrics.advances[ch] ?? metrics.average;
    if (prev !== null) units += metrics.kerning[prev + ch] ?? 0;
    units += PLATE_TRACKING * 1000;
    prev = ch;
  }
  return (units / 1000) * size;
}

export interface PlateWrap {
  /** Lines the string occupies at this size in this width. */
  lines: number;
  /** The widest line, in px. Exceeds `usable` only when a single word does. */
  widest: number;
}

/**
 * A run of text the browser will not break inside, plus what joins it to the
 * run before: a space, or nothing when the break opportunity was a hyphen.
 */
interface PlateAtom {
  text: string;
  glue: "" | " ";
}

/** Longest trailing word that is kept with the word before it. */
export const TAIL_KEEP_MAX = 1;

/**
 * Offsets inside one whitespace-free word where the browser may break: just
 * after a hyphen-minus or en dash that `plateAtoms` below accepts.
 */
function inWordBreaks(word: string): number[] {
  const breaks: number[] = [];
  for (let i = 1; i < word.length - 1; i++) {
    const ch = word[i];
    if (ch !== "-" && ch !== "–") continue;
    if (!/[\p{L}\p{N}]/u.test(word[i - 1])) continue;
    if (!(ch === "–" ? /[\p{L}\p{N}]/u : /\p{L}/u).test(word[i + 1])) continue;
    breaks.push(i + 1);
  }
  return breaks;
}

/**
 * The text split where `plateAtoms` refuses to break: `head` wraps freely,
 * `tail` is the last word with the run before it (back to the last hyphen
 * break, the same ones `plateAtoms` takes), or "" when nothing is kept.
 * `head + tail` is always `text` exactly.
 */
export function splitPlateTail(text: string): { head: string; tail: string } {
  const m = new RegExp(`^([\\s\\S]*?)(\\S+)(\\s+\\S{1,${TAIL_KEEP_MAX}})$`).exec(text);
  if (!m) return { head: text, tail: "" };
  const [, lead, prev, last] = m;
  const cut = inWordBreaks(prev).at(-1) ?? 0;
  return { head: lead + prev.slice(0, cut), tail: prev.slice(cut) + last };
}

/**
 * The break opportunities the model takes, which are a SUBSET of the
 * browser's (UAX #14 as Chromium applies it, verified in a render):
 *
 *   - after whitespace, always;
 *   - after a hyphen-minus between a letter or digit and a letter
 *     ("lock-chime" → "lock-" / "chime"; UAX #14 LB25 forbids the break
 *     before a digit, so "-1" stays whole);
 *   - after an en dash between a letter or digit and a letter or digit
 *     ("2022–2024" → "2022–" / "2024", which the render confirmed).
 *
 * Every break this takes is one the browser can take, so the model never
 * predicts fewer lines than the browser renders, which is the direction that
 * would make the server's size overrun on the client. Where the browser has
 * an opportunity this lacks (an em dash inside a word, a slash) the only cost
 * is a size or two of plate left on the table.
 */
function plateAtoms(text: string): PlateAtom[] {
  const atoms: PlateAtom[] = [];
  const words = text.trim().split(/\s+/);
  // A trailing one-character word ("Y" in "Model Y") never wraps onto
  // a line of its own: it travels with the word before it. plate.tsx renders
  // the same pair inside a nowrap span so the browser agrees with this model.
  if (words.length >= 2 && words[words.length - 1].length <= TAIL_KEEP_MAX) {
    const [tail] = words.splice(-1);
    words[words.length - 1] += ` ${tail}`;
  }
  for (const word of words) {
    if (!word) continue;
    let start = 0;
    for (const end of inWordBreaks(word)) {
      atoms.push({ text: word.slice(start, end), glue: start === 0 ? " " : "" });
      start = end;
    }
    atoms.push({ text: word.slice(start), glue: start === 0 ? " " : "" });
  }
  if (atoms.length) atoms[0].glue = "";
  return atoms;
}

/**
 * Break a string into lines the way the plate's CSS makes the browser do it:
 * greedily, at the opportunities `plateAtoms` describes.
 *
 * `overflow-wrap: normal` and `word-break: normal` (plate.tsx) mean a word
 * never breaks inside itself; one too wide for the container overflows its
 * line box instead, which is what `widest` reports. Each line is measured as
 * the string it actually holds — its internal spaces with their own advance,
 * tracking and kerning, and no trailing space, which the browser hangs
 * outside the line box exactly as `getClientRects()` reports it.
 */
export function wrapPlate(
  text: string,
  size: number,
  usable: number,
  metrics: PlateFontMetrics = ARCHIVO_PLATE_METRICS,
): PlateWrap {
  const atoms = plateAtoms(text);
  if (atoms.length === 0) return { lines: 1, widest: 0 };

  let lines = 1;
  let widest = 0;
  let line = atoms[0].text;
  for (let i = 1; i < atoms.length; i++) {
    const candidate = line + atoms[i].glue + atoms[i].text;
    if (plateLineWidth(candidate, size, metrics) <= usable) {
      line = candidate;
    } else {
      widest = Math.max(widest, plateLineWidth(line, size, metrics));
      line = atoms[i].text;
      lines += 1;
    }
  }
  widest = Math.max(widest, plateLineWidth(line, size, metrics));
  return { lines, widest };
}

/** Below this the treatment stops being a plate. §01.4, §08.2. */
export const PLATE_FLOOR = 56;

/** The plate-fallback treatment, which is NOT type-h1. §08.2. */
const FALLBACK_DESKTOP = 46;
const FALLBACK_MOBILE = 31;

/**
 * The fallback size for a breakpoint, keyed on its plate cap.
 *
 * Keyed on the desktop cap rather than `>= 92`, because the mobile cap is
 * itself 92 and that comparison silently handed mobile the desktop size.
 */
export function plateFallbackFor(cap: number): number {
  return cap >= 184 ? FALLBACK_DESKTOP : FALLBACK_MOBILE;
}

/** The plate's line height. §01.3. Load-bearing here, not decorative. */
export const PLATE_LH = 0.86;

/**
 * How tall the plate is allowed to get, in px, per breakpoint.
 *
 * This replaces the four-line rule as the binding constraint, because a line
 * count does not describe the thing that actually went wrong. The pilot on
 * /sounds/[id] honoured four lines exactly and still produced a 449px heading,
 * because four lines of 120px type is 412px however you count it. Lines are a
 * proxy; height is the quantity the page cares about, since what it costs is
 * the player's distance from the fold on the page that earns the money.
 *
 * Desktop is the tighter budget of the two despite the larger viewport. The
 * detail hero shares its row with the player, so height there pushes the
 * primary action down; on mobile the player is below the fold regardless and
 * the plate is the whole first impression.
 *
 * Mobile is set above what the current catalog actually produces (the 390px
 * render measures 213px), so this changes desktop and leaves the mobile
 * treatment that already reads correctly alone.
 */
const HEIGHT_BUDGET_DESKTOP = 260;
const HEIGHT_BUDGET_MOBILE = 230;

/** Height budget for a breakpoint, keyed on its plate cap as the fallback is. */
export function plateHeightBudgetFor(cap: number): number {
  return cap >= 184 ? HEIGHT_BUDGET_DESKTOP : HEIGHT_BUDGET_MOBILE;
}

/**
 * A plate may wrap this far and no further, as a secondary guard.
 *
 * The height budget is the real constraint and normally binds first. This
 * stays as a ceiling so that a wide container with a short string cannot
 * produce a stack of lines that satisfies the budget arithmetically while
 * looking nothing like a plate.
 */
export const MAX_LINES = 4;

export interface PlateFit {
  /** Rendered font size in px. */
  size: number;
  /** False when the string fell through the floor and became plate-fallback. */
  isPlate: boolean;
  /** Lines the string is expected to occupy at `size`. */
  lines: number;
  /** Height the plate is expected to occupy at `size`, in px. */
  height: number;
}

/** Lines a string of `chars` occupies at `size` in a container `usable` wide. */
function linesAt(chars: number, size: number, usable: number, coef = COEF): number {
  return Math.max(1, Math.ceil((chars * coef * size) / usable));
}

/**
 * The longest word in a string, in characters.
 *
 * "Word" means whitespace-separated: punctuation inside a token (a hyphen, an
 * apostrophe) does not create a break opportunity in the plate's type, which
 * renders with `overflow-wrap: normal` — see plate.tsx. So the fit test has
 * to reason about the same units the browser actually refuses to split.
 */
function longestWordChars(text: string): number {
  let longest = 0;
  for (const word of text.split(/\s+/)) {
    if (word.length > longest) longest = word.length;
  }
  return longest;
}

/**
 * Solve the plate size for a string in a container.
 *
 * The token alone is not enough. A fixed 92px plate renders a 39-character
 * display name as twelve lines and 949px of height at 390px wide, which is
 * taller than the phone it is on. The ladder in §08.2 and the clamp in §10.2
 * exist for exactly this, and both are load-bearing rather than decorative.
 *
 * Solves for the largest size that fits the string in at most four lines,
 * capped by the breakpoint's plate cap. If that lands under the 56px floor the
 * string is too long to be a plate at this width, and it returns the
 * plate-fallback size instead with `isPlate: false`. Callers must honour that
 * flag: the fallback is a heading, so it drops the uppercase plate treatment.
 *
 * Given `text`, the fit test is the font's own arithmetic: `wrapPlate` breaks
 * the string at the same places the browser will, using measured advances
 * and kerning, so the size this returns is the size the client's measurement
 * confirms rather than a first guess it corrects. That is what keeps the
 * server-rendered heading and the hydrated one the same height — the client
 * still bisects on what the browser rendered (PRODUCT.md), but against this
 * model it finds nothing to correct.
 *
 * A size only counts as fitting when its longest WORD also fits the container
 * on its own line, not merely when the line count comes out acceptable. With
 * `overflow-wrap: normal` an unfittable word does not wrap, it overflows, so a
 * single 20-character word ("SHUTDOWN"-scale per PRODUCT.md's "longest single
 * unbroken word: 20 characters") *is* the line, and one line too wide is still
 * an overrun even though the ceiling and the height budget both read as fine.
 *
 * Without `text` the character-count model (`chars × COEF × size`) is used,
 * exactly as before; every pre-existing caller that omits it gets the same
 * answer it always did.
 *
 * `usable` is the content width in px, gutters already subtracted.
 */
export function solvePlate({
  chars,
  usable,
  cap,
  coef = COEF,
  text,
}: {
  chars: number;
  usable: number;
  cap: number;
  /**
   * Override the advance-width constant. The client passes a value calibrated
   * from what the browser actually rendered, which beats the average.
   */
  coef?: number;
  /**
   * The actual string, when available, so the solver can check the longest
   * word against the container independently of the character-count model.
   * Optional and additive: omitting it (as every pre-existing caller and test
   * does) preserves the exact prior behaviour, because `chars` alone cannot
   * recover word boundaries after the fact.
   */
  text?: string;
}): PlateFit {
  if (chars <= 0 || usable <= 0) {
    return { size: cap, isPlate: true, lines: 1, height: Math.round(cap * PLATE_LH) };
  }

  const budget = plateHeightBudgetFor(cap);

  // Lines and the widest line at a size. With the string, the measured font;
  // without it, the character-count model (and the longest word by count, so
  // a size whose longest word cannot fit one line is still not a fit).
  const longestWord = text ? longestWordChars(text) : 0;
  const wrapAt = (size: number): PlateWrap =>
    text
      ? wrapPlate(text, size, usable)
      : { lines: linesAt(chars, size, usable, coef), widest: longestWord * coef * size };

  // Descend from the cap rather than solving in closed form. Height is
  // lines * size * lh and lines is a ceiling, so height moves in steps, not
  // smoothly: the algebraic answer routinely lands just inside a step boundary
  // and rounds back out of budget. Walking down finds the largest size that
  // actually satisfies both constraints. At most ~130 integer iterations.
  //
  // Note the direction: a SMALLER size wraps to FEWER lines, not more, because
  // narrower glyphs pack more per line. So the line ceiling and the height
  // budget both bound the size from above, and the search only ever descends.
  for (let size = Math.floor(cap); size >= PLATE_FLOOR; size--) {
    const { lines, widest } = wrapAt(size);
    if (lines > MAX_LINES) continue;
    const height = Math.round(lines * size * PLATE_LH);
    if (height > budget) continue;
    if (widest > usable) continue;
    return { size, isPlate: true, lines, height };
  }

  // Nothing between the cap and the floor satisfies the budget, so this string
  // is not a plate at this width. The fallback is a heading, not a small plate:
  // callers must drop the uppercase treatment on isPlate: false. Its line
  // count is an estimate (the fallback is sentence case at weight 800, the
  // table is uppercase at 900, so this reads slightly high); nothing lays out
  // from it, the fallback's size is fixed.
  const fallback = plateFallbackFor(cap);
  const { lines } = wrapAt(fallback);
  return {
    size: fallback,
    isPlate: false,
    lines,
    height: Math.round(lines * fallback * PLATE_LH),
  };
}

/** The two breakpoints the server pass solves for. §01.3, §01.4. */
export const PLATE_MOBILE_CAP = 92;
export const PLATE_DESKTOP_CAP = 184;

/**
 * Viewport width at which the plate cap changes, in px. Mirrors the
 * `@media (min-width: 768px)` breakpoint rule Plate renders for the
 * sm/md type variables.
 */
export const PLATE_MD_BREAKPOINT = 768;
